import $ from 'jquery'
import LayerGroup from 'ol/layer/Group'
import * as olHas from 'ol/has'
import ImageWMS from 'ol/source/ImageWMS'
import TileWMS from 'ol/source/TileWMS'
import WMTS from 'ol/source/WMTS'
import VectorSource from 'ol/source/Vector'
import Icon from 'ol/style/Icon'
import Polygon from 'ol/geom/Polygon'
import Tool from '../Tool'
import Spinner from '../ui/Spinner'
import encoders from './printEncoders'
import { unByKey } from 'ol/Observable'
import { getVectorContext } from 'ol/render'
import Point from 'ol/geom/Point'
import VectorLayer from 'ol/layer/Vector'
import Feature from 'ol/Feature'
import { Fill, Stroke, Circle, Style } from 'ol/style'
import * as olColor from 'ol/color'

/**
 * Print tool class
 * Uses mapfish service
 * @param {Object} config
 * @constructor
 */
export default class Print extends Tool {
  constructor(config) {
    super()

    this.enableToggle = false

    $.extend(this, config)

    this.$printModal = $('#print-settings-modal')
    this.$printToggleButton = $('#print-toggle-button')
    this.$printCancelButton = this.$printModal.find('.print-cancel-button')
    this.$printButton = $('#print-map')
    this.$printPreviewButton = $('#print-map-preview')
    this.$printToolbar = $('#print-preview-toolbar')
    this.$printToolbarCancelButton = $('#print-preview-toolbar-cancel')
    this.$printToolbarConfirmButton = $('#print-preview-toolbar-confirm')
    this.$printTitleInput = $('#print-title')
    this.$printFileFormatRadios = $('#print-setting-file-format-wrapper')
    this.$printSizeWrapper = $('#print-setting-size-wrapper')
    this.$navbarNorth = $('#navbarNorth')
    this.$navbarSouth = $('#navbarSouth')
    this.$map = $('#map')

    this.config = this.config || {}
    this.config.layout = this.config.layout || 'A4 portrait'

    this.encoders = encoders

    this.POINTS_PER_INCH = 72 // PostScript points 1/72"
    this.MM_PER_INCHES = 25.4
    this.UNITS_RATIO = 39.37 // inches per meter
    this.PRINT_PREVIEW_PADDING = 25

    // Mapfish spec object
    this.spec = {
      layout: null,
      title: '',
      srs: this.config.srs,
      units: this.config.units,
      outputFilename: this.config.outputFilename,
      outputFormat: this.config.outputFormat,
      layers: [],
      pages: [
        {
          center: [],
          scale: null,
          dpi: this.config.dpi,
          mapTitle: this.config.title,
          comment: null,
          data: []
        }
      ]
    }

    // Mapfish properties
    this.mapfish = {
      settings: null,
      layout: {},
      scale: {},
      printRectangle: null
    }

    // Array for storing event listeners
    this.previewEvents = []

    // Gets Mapfish service settings
    this.getServiceSettings(
      function(error) {
        if (!error) {
          this.activate()
        }
      }.bind(this)
    )

    this.toggleNavbarSouthPrintIcon()

    this.$printPreviewButton.on('click', this.previewShow.bind(this))
    this.$printToggleButton.on('click', this.openPrintModal.bind(this))
    this.$printCancelButton.on('click', this.closePrintModal.bind(this))

    this.centrumPoint = new Feature({
      geometry: new Point([4, 4])
    })
    var centrumPointStyle = new Style({
      image: new Circle({
        radius: 7,
        fill: new Fill({ color: [0, 0, 0, 0] }),
        stroke: new Stroke({
          color: [255, 0, 0, 0],
          width: 2
        })
      })
    })
    this.centrumPoint.setStyle(centrumPointStyle)
    this.printLayer = new VectorLayer({
      name: 'print',
      source: new VectorSource({ features: [this.centrumPoint] })
    })
    this.map.addLayer(this.printLayer)
  }

  openPrintModal() {
    this.$printModal.css('display', 'block')
    setTimeout(() => {
      this.$printModal.addClass('in')
    }, 100)
  }

  closePrintModal() {
    this.$printModal.css('display', 'none')
    this.$printModal.removeClass('in')
  }

  /**
   * Activates print after mapfish settings are downloaded from service
   * Creates dynamic options in print modal and initialize button events
   */
  activate() {
    if (!this.activated) {
      this.$printToolbarCancelButton.on('click', this.previewHide.bind(this))
      this.$printToolbarConfirmButton.on('click', this.print.bind(this))
      this.activated = true
    }
  }
  /**
   * Parses visible layers and users into a the spec object and sends it to Mapfish service
   */
  print() {
    this.printLayer.getSource().removeFeature(this.centrumPoint)
    var encLayers = []
    var view = this.map.getView()
    var proj = view.getProjection()
    var layers = this.map.getLayers()
    
    // Checks if layer is visible and encodes layer to Mapfish spec
    layers.forEach(
      function(layer) {
        if (layer.getVisible()) {
          if (layer instanceof LayerGroup) {
            var encs = this.encoders.layers['Group'].call(this, layer, proj)
            encLayers = encLayers.concat(encs)
          } else {
            var enc = this.encodeLayer(layer, proj)
            if (enc && enc.layer) {
              encLayers.push(enc.layer)
            }
          }
        }
      }.bind(this)
    )
    this.printLayer.getSource().addFeature(this.centrumPoint)
    this.spec.layers = encLayers

    this.setUserGeneratedPrintSettings()

    // Updates print scale
    this.previewUpdate()

    console.log('spec', this.spec)
    this.mapfishPrint()
  }

  /**
   * Get selected layout from config or return default layout
   * @param  {array} layouts List of layouts response from Mapfish print service
   * @return {object} layout Seleced layout
   */
  getSelectedLayout(layouts) {
    return layouts.filter(layout => this.config.layout === layout.name)[0]
  }

  /**
   * Loads settings from Mapfish service
   */
  getServiceSettings(callback) {
    var settingsUrl = this.config.settingsUrl

    $.getJSON(
      settingsUrl,
      function(settings) {
        var layout = this.getSelectedLayout(settings.layouts)
        if (!layout) {
          callback(
            `Ingen utskrifts-layout hittades med namn "${this.config.layout}"`
          )
        } else {
          this.mapfish.settings = settings
          this.mapfish.layout = this.getSelectedLayout(settings.layouts)
          this.spec.layout = this.mapfish.layout ? this.mapfish.layout.name : ''
          callback()
        }
      }.bind(this)
    ).fail(function(error) {
      console.error(error)
      callback(
        'Misslyckades att kontakta utskriftstjänsten. Var vänlig försök igen senare.'
      )
    })
  }
  /**
   * Get layer source
   * @param  {ol.layer} layer
   * @return {ol.source}  URL with fully qualified domain name
   */
  getLayerSource(layer) {
    var pattern, source, currentHost

    source = layer.getSource()
    pattern = /http/i
    currentHost = window.location.protocol + '//' + window.location.host

    if (source.urls_ && source.urls_[0]) {
      if (!pattern.test(source.urls_[0])) {
        source.urls_[0] = currentHost + source.urls_[0]
      }
    } else {
      if (!pattern.test(source.url_)) {
        source.url_ = currentHost + source.url_
      }
    }

    return source
  }
  /**
   * Get layer styles
   * @param  {ol.layer} layer
   * @return {array}  Array with style names
   */
  getLayerStyles(layer) {
    var styles = []
    if (layer) {
      var olga_ = layer.get('olga_')
      if (
        olga_ &&
        olga_.source &&
        olga_.source.params &&
        olga_.source.params.STYLES
      ) {
        styles = olga_.source.params.STYLES.split(',')
      }
    }
    return styles
  }
  /**
   * Resets print preview box context before each compose
   */
  handlePreCompose(evt) {
    var ctx = evt.context
    ctx.save()
  }
  /**
   * Draws print preview box on each compose
   */
  handlePostCompose(evt) {
    if (!this.mapfish.printRectangle) {
      return
    }
    var ctx = evt.context
    var size = this.map.getSize()
    var height = size[1] * olHas.DEVICE_PIXEL_RATIO
    var width = size[0] * olHas.DEVICE_PIXEL_RATIO

    var minx, miny, maxx, maxy
    var printRectangle = this.calculateBoundsPixels(1)
    minx = printRectangle[0]
    miny = printRectangle[1]
    maxx = printRectangle[2]
    maxy = printRectangle[3]

    ctx.beginPath()

    // Outside polygon, must be clockwise
    ctx.moveTo(0, 0)
    ctx.lineTo(width, 0)
    ctx.lineTo(width, height)
    ctx.lineTo(0, height)
    ctx.lineTo(0, 0)
    ctx.closePath()

    // Inner polygon,must be counter-clockwise
    ctx.moveTo(minx, miny)
    ctx.lineTo(minx, maxy)
    ctx.lineTo(maxx, maxy)
    ctx.lineTo(maxx, miny)
    ctx.lineTo(minx, miny)
    ctx.closePath()

    ctx.fillStyle = 'rgba(0, 5, 25, 0.75)'
    ctx.fill()

    ctx.restore()
  }
  /**
   * Get center coordinate
   */
  getCenterCoordinate() {
    var center = this.map.getView().getCenter()
    return center
  }
  /**
   * Start drawing print preview box
   */
  previewEventsActivate() {
    this.previewEventsDeactivate()
    this.previewEvents = [
      this.printLayer.on('prerender', this.handlePreCompose.bind(this)),
      this.printLayer.on('postrender', this.handlePostCompose.bind(this)),
      this.map.on('moveend', this.previewUpdate.bind(this)),
      this.map.on('change:size', this.previewUpdate.bind(this))
    ]
    //this.printLayer.getSource().addFeature(this.centrumPoint)
    this.centrumPoint.setGeometry(new Point(this.getCenterCoordinate()))
  }
  /**
   * Stop drawing print preview box
   */
  previewEventsDeactivate() {
    var deregister = this.previewEvents
    if (deregister && deregister.length > 0) {
      for (var i = 0; i < deregister.length; i++) {
        unByKey(deregister[i])
      }
    }

    this.previewEvents = []

    this.map.render()
  }
  /**
   * Shows print preivew and print toolbar
   */
  previewShow() {
    if (!this.mapfish.settings) {
      this.getServiceSettings(
        function(error) {
          if (error) {
            window.alert(error)
          } else {
            this.activate()
            this.previewShow()
          }
        }.bind(this)
      )
      return
    }

    this.closePrintModal()

    this.$navbarNorth
      .find('.navbar-collapse')
      .removeClass('in')
      .addClass('collapse')

    this.spec.pages[0].scale = this.calculateScale()

    this.updatePrintRectanglePixels()

    this.printToolbarToggle(true)
    this.printToolbarSetPosition()

    this.previewEventsActivate()
  }
  /**
   * Update preivew box and button positions
   */
  previewUpdate() {
    this.centrumPoint.setGeometry(new Point(this.getCenterCoordinate()))
    this.spec.pages[0].scale = this.calculateScale()
    this.updatePrintRectanglePixels()
    this.printToolbarSetPosition()
  }
  /**
   * Hides print preview box
   */
  previewHide() {
    this.previewEventsDeactivate()
    this.printToolbarToggle(false)
  }
  /**
   * Calculate and saves optimal print scale based on current window size
   */
  calculateScale() {
    var size = this.mapfish.layout.map // papersize in dot!
    var view = this.map.getView()
    var resolution = view.getResolution()
    var [width] = this.getMaxSize()
    return (
      (width /
        (((size.width / this.POINTS_PER_INCH) * this.MM_PER_INCHES) / 1000.0)) *
      resolution
    )
  }

  /**
   * Calculate the total height of both navbars
   * @return {Number}
   */
  getNavbarsTotalHeight() {
    var height = 0

    if (this.$navbarNorth.is(':visible') || this.$navbarSouth.is(':visible')) {
      height += this.$navbarNorth.height()
      height += this.$navbarSouth.height()
    }

    return height
  }

  /**
   * Calculate max width and height of preview window based on print layout proportions
   * @return {Array}
   */
  getMaxSize() {
    var printSize = this.mapfish.layout.map // papersize in dot!
    var mapSize = this.map.getSize()
    var scale = Math.min(
      mapSize[0] / printSize.width,
      mapSize[1] / printSize.height
    )
    var w = printSize.width * scale - this.PRINT_PREVIEW_PADDING
    var h = printSize.height * scale - this.PRINT_PREVIEW_PADDING

    return [w, h]
  }

  /**
   * Calculate current screen size (with retina support)
   */
  calculateBoundsPixels(pixelRatio = olHas.DEVICE_PIXEL_RATIO) {
    var mapSize = this.map.getSize()
    var center = [(mapSize[0] * pixelRatio) / 2, (mapSize[1] * pixelRatio) / 2]

    var minx, miny, maxx, maxy

    var [w, h] = this.getMaxSize()
    minx = center[0] - w / 2
    miny = center[1] - h / 2
    maxx = center[0] + w / 2
    maxy = center[1] + h / 2

    return [minx, miny, maxx, maxy]
  }

  /**
   * Update print rectagle size and trigger a redraw
   */
  updatePrintRectanglePixels() {
    this.mapfish.printRectangle = this.calculateBoundsPixels()
    this.map.render()
  }
  /**
   * Get print preview center coordinates
   */
  getPrintRectangleCenterCoord() {
    var printRectangle = this.mapfish.printRectangle
    var bottomLeft = printRectangle.slice(0, 2)
    var width = printRectangle[2] - printRectangle[0]
    var height = printRectangle[3] - printRectangle[1]
    var center = [bottomLeft[0] + width / 2, bottomLeft[1] + height / 2]

    // convert back to map display size
    var mapPixelCenter = [
      center[0] / olHas.DEVICE_PIXEL_RATIO,
      center[1] / olHas.DEVICE_PIXEL_RATIO
    ]

    return this.map.getCoordinateFromPixel(mapPixelCenter)
  }
  /**
   * Toogles print toolbar visibility
   * @param {bool} state
   */
  printToolbarToggle(state) {
    if (state === true) {
      this.$printToolbar.show()
    } else if (state === false) {
      this.$printToolbar.hide()
    } else {
      this.$printToolbar.toggle()
    }
    this.$printToolbar.css('position', 'absolute')
  }
  /**
   * Sets print toolbar position relative to print preview box
   */
  printToolbarSetPosition() {
    var printRectangle = this.calculateBoundsPixels(1)
    var mapPosition = this.$map.offset()
    var mapWidth = $('.ol-viewport:first').width()
    var toolbarWidth = this.$printToolbar.width()
    var navbarNorthHeight = 0

    if (this.$navbarNorth.is(':visible')) {
      navbarNorthHeight = this.$navbarNorth.height()
    }

    var left = mapWidth / 2 - toolbarWidth / 2

    // Sets position
    this.$printToolbar.css({
      position: 'absolute',
      top: 65,
      left: left
    })
  }
  /**
   * Gets and sets user generated print settings
   */
  setUserGeneratedPrintSettings() {
    var center = this.getPrintRectangleCenterCoord()

    this.spec.pages[0].center = center

    this.spec.pages[0].mapTitle = this.$printTitleInput.val()
  }
  /**
   * Sends spec object to mapfish service and sends user to download url
   */
  mapfishPrint() {
    var createUrl = this.config.createUrl
    var spinner = new Spinner()
    var data = JSON.stringify(this.spec)

    // Needed for Mapfish to accept '%' in layerdefs...
    data = data.replace(/%/g, '%25')

    spinner.start()
    $.ajax({
      type: 'POST',
      url: createUrl,
      data: 'spec=' + data
    })
      .done(
        function(data) {
          spinner.stop()
          this.downloadUrl(data.getURL)
        }.bind(this)
      )
      .fail(function() {
        spinner.stop()
        window.alert('Print failed')
      })
  }
  /**
   * Sends user to generated url
   * @param {String} url
   */
  downloadUrl(url) {
    if (window.location) {
      window.location = url
    } else {
      window.open(url)
    }
  }
  /**
   * Main method for ecoding layers
   * @param {ol.layer} layer
   * @param {ol.proj} proj
   * @return {Object} ecnoded layer
   */
  encodeLayer(layer) {
    var encLayer, viewparams, cqlparams, styles, layerdefs, access_token
    var view = this.map.getView()
    var ext = view.calculateExtent(this.map.getSize())
    var resolution = this.map.getView().getResolution()

    if (!(layer instanceof LayerGroup)) {
      var src = this.getLayerSource(layer)
      var layerConfig = {}
      var minResolution = layer.get('minResolution') || 0
      var maxResolution = layer.get('maxResolution') || Infinity

      // Only print layers within current resolution
      if (resolution < maxResolution && resolution >= minResolution) {
        if (src instanceof ImageWMS || src instanceof TileWMS) {
          encLayer = this.encoders.layers['WMS'].call(this, layer, layerConfig)
        } else if (src instanceof VectorSource) {
          var features = []

          // Only print layers within current extent
          if (ext) {
            src.forEachFeatureInExtent(ext, function(feat) {
              features.push(feat)
            })
          } else {
            src.forEachFeature(function(feat) {
              features.push(feat)
            })
          }

          // Encode found features
          if (features && features.length > 0) {
            encLayer = this.encoders.layers['Vector'].call(
              this,
              layer,
              features
            )
          }
        } else if (src instanceof WMTS){
         
          let layerName = layer.getSource().getLayer()
          if (layerName === 'topowebb' || layerName === 'topowebb_nedtonad') {
            encLayer = this.encoders.layers['WMTS'].call(
              this,
              layer,
              layerConfig
            )
          }
          return { layer: encLayer }
        }

        if (encLayer) {
          encLayer.customParams = {}

          // Gets styles and custom params from config
          viewparams = this.getLayerViewparams(layer)
          cqlparams = this.getLayerCqlparams(layer)
          styles = this.getLayerStyles(layer)
          layerdefs = this.getLayerdefs(layer)
          access_token = this.getAccessToken(layer)

          if (viewparams) {
            encLayer.customParams.viewparams = viewparams
          }

          if (cqlparams) {
            encLayer.customParams.CQL_FILTER = cqlparams
          }

          if (styles && styles.length) {
            encLayer.styles = styles
          }

          if (layerdefs) {
            encLayer.customParams.layerdefs = layerdefs
          }

          if (access_token) {
            encLayer.customParams.access_token = access_token
          }
        }
      }
    }
    return { layer: encLayer }
  }
  /**
   * Prepend url with fully qualified domain name if needed
   * @param  {string} url
   * @return {string}  URL with fully qualified domain name
   */
  prependHostnameToUrl(url) {
    var pattern = /^https?:\/\//i

    if (!pattern.test(url)) {
      var currentHost = window.location.protocol + '//' + window.location.host
      url = currentHost + url
    }

    return url
  }
  /**
   * Transform feature to print literal
   * @param {ol.feature} feature
   * @param {ol.style} style
   * @return {Object} literal
   */
  transformToPrintLiteral(feature, style) {
    /**
     * ol.style.Style properties:
     *
     *  fill: ol.style.Fill :
     *    fill: String
     *  image: ol.style.Image:
     *    anchor: array[2]
     *    rotation
     *    size: array[2]
     *    src: String
     *  stroke: ol.style.Stroke:
     *    color: String
     *    lineCap
     *    lineDash
     *    lineJoin
     *    miterLimit
     *    width: Number
     *  text
     *  zIndex
     */

    /**
     * Print server properties:
     *
     * fillColor
     * fillOpacity
     * strokeColor
     * strokeOpacity
     * strokeWidth
     * strokeLinecap
     * strokeLinejoin
     * strokeDashstyle
     * pointRadius
     * label
     * fontFamily
     * fontSize
     * fontWeight
     * fontColor
     * labelAlign
     * labelOutlineColor
     * labelOutlineWidth
     * graphicHeight
     * graphicOpacity
     * graphicWidth
     * graphicXOffset
     * graphicYOffset
     * zIndex
     */
    var literal = {
      zIndex: style.getZIndex()
    }
    var fill = style.getFill()
    var stroke = style.getStroke()
    var textStyle = style.getText()
    var imageStyle = style.getImage()
    var color
    if (imageStyle) {
      var size = imageStyle.getSize ? imageStyle.getSize() : 1
      var anchor = imageStyle.getAnchor ? imageStyle.getAnchor() : false
      var scale = imageStyle.getScale()
      literal.rotation = imageStyle.getRotation()
      if (size) {
        literal.graphicWidth = size[0] * scale
        literal.graphicHeight = size[1] * scale
      }
      if (anchor) {
        literal.graphicXOffset = -anchor[0] * scale
        literal.graphicYOffset = -anchor[1] * scale
      }
      if (imageStyle instanceof Icon) {
        // literal.externalGraphic = imageStyle.getSrc()
        // TODO: Send as base64 after Mapfish upgrade to 2.x
        // const canvas = document.createElement('canvas')
        // const ctx = canvas.getContext('2d')
        // ctx.drawImage(imageStyle.getImage(), 10, 10)
        // literal.externalGraphic = imageData
        // literal.fillOpacity = 1
      } else {
        // ol.style.Circle
        fill = imageStyle.getFill()
        stroke = imageStyle.getStroke()
        literal.pointRadius = imageStyle.getRadius()
      }
    }

    if (fill) {
      color = olColor.asArray(fill.getColor())
      literal.fillColor = this.toHexa(color)
      literal.fillOpacity = color[3]
    } else if (!literal.fillOpacity) {
      literal.fillOpacity = 0 // No fill
    }

    if (stroke) {
      color = olColor.asArray(stroke.getColor())
      literal.strokeWidth = stroke.getWidth()
      literal.strokeColor = this.toHexa(color)
      literal.strokeOpacity = color[3]
      literal.strokeLinecap = stroke.getLineCap() || 'round'
      literal.strokeLinejoin = stroke.getLineJoin() || 'round'

      if (stroke.getLineDash()) {
        literal.strokeDashstyle = 'dash'
      }
      // TO FIX: Not managed by the print server
      // literal.strokeMiterlimit = stroke.getMiterLimit()
    } else {
      literal.strokeOpacity = 0 // No Stroke
    }

    if (textStyle) {
      var fillColor = olColor.asArray(textStyle.getFill().getColor())

      var fontValues = textStyle.getFont().split(' ')
      literal.fontColor = this.toHexa(fillColor)
      // Fonts managed by print server: COURIER, HELVETICA, TIMES_ROMAN

      literal.fontFamily = fontValues[2].toUpperCase()
      literal.fontSize = parseInt(fontValues[1], 10)
      literal.fontWeight = fontValues[0]
      literal.label = textStyle.getText()
      literal.labelAlign = textStyle.getTextAlign()

      // Stroke
      if (textStyle.getStroke()) {
        var strokeColor = olColor.asArray(textStyle.getStroke().getColor())
        literal.labelOutlineColor = this.toHexa(strokeColor)
        literal.labelOutlineWidth = textStyle.getStroke().getWidth()
      }
    }
    return literal
  }
  /**
   * Convert color to hex
   * @param {String} Color
   * @return {String} hex
   */
  toHexa(olColor) {
    var hex = '#'
    for (var i = 0; i < 3; i++) {
      var part = olColor[i].toString(16)
      if (part.length === 1 && parseInt(part, 10) < 10) {
        hex += '0'
      }
      hex += part
    }
    return hex
  }
  /**
   * Convert circle to polygon
   * @param {ol.geom.Circle} circle
   * @param {Number} sides
   * @param {Number} rotation
   * @return {ol.geom.Polygon} polygon
   */
  circleToPolygon(circle, sides, rotation) {
    var origin = circle.getCenter()
    var radius = circle.getRadius()
    sides = sides || 40
    var angle = Math.PI * (1 / sides - 1 / 2)
    if (rotation) {
      angle += (rotation / 180) * Math.PI
    }
    var points = []
    for (var i = 0; i < sides; ++i) {
      var rotatedAngle = angle + (i * 2 * Math.PI) / sides
      var x = origin[0] + radius * Math.cos(rotatedAngle)
      var y = origin[1] + radius * Math.sin(rotatedAngle)
      points.push([x, y])
    }
    points.push(points[0]) // Close the polygon

    return new Polygon([points])
  }
  /**
   * Get layer view parameters specified in config
   * @param  {ol.layer} layer
   * @return {String}
   */
  getLayerViewparams(layer) {
    var viewparams

    if (layer) {
      var olga_ = layer.get('olga_')

      if (olga_ && olga_.source && olga_.source.params) {
        viewparams = this.getValueByCaseInsensitiveKey(
          olga_.source.params,
          'viewparams'
        )
      }
    }

    return viewparams
  }
  /**
   * Get layer CQL parameters specified in config
   * @param  {ol.layer} layer
   * @return {String}
   */
  getLayerCqlparams(layer) {
    var cqlparams

    if (layer) {
      var olga_ = layer.get('olga_')

      if (olga_ && olga_.source && olga_.source.params) {
        cqlparams = this.getValueByCaseInsensitiveKey(
          olga_.source.params,
          'cql_filter'
        )
      }
    }

    return cqlparams
  }

  /**
   * Get layer CQL parameters specified in config
   * @param  {ol.layer} layer
   * @return {String}
   */
  getLayerdefs(layer) {
    var layerdefs

    if (layer) {
      var olga_ = layer.get('olga_')

      if (olga_ && olga_.source && olga_.source.params && olga_.source.params) {
        layerdefs = this.getValueByCaseInsensitiveKey(
          olga_.source.params,
          'layerdefs'
        )
      }
    }

    return layerdefs
  }

  /**
   * Get access_token parameters specified in config
   * @param  {ol.layer} layer
   * @return {String}
   */
  getAccessToken(layer) {
    var access_token

    if (layer) {
      var olga_ = layer.get('olga_')

      if (olga_ && olga_.source && olga_.source.params && olga_.source.params) {
        access_token = this.getValueByCaseInsensitiveKey(
          olga_.source.params,
          'access_token'
        )
      }
    }

    return access_token
  }

  /**
   * Returns an object property value by a case insensitive key
   * @param  {object} object
   * @param  {string} key
   * @return {String}
   */
  getValueByCaseInsensitiveKey(obj, key) {
    return Object.keys(obj).reduce(
      (result, k) => (k.toLowerCase() === key.toLowerCase() ? obj[k] : result),
      undefined
    )
  }

  /**
   * Add print button to south navbar if north navbar is hidden
   */
  toggleNavbarSouthPrintIcon() {
    var navbarNorthIsVisible = $('body').hasClass('navbarNorth')

    if (!navbarNorthIsVisible) {
      const li = $('<li></li>')
      this.$printToggleButton
        .find('span:last')
        .addClass('hidden-xs')
        .addClass('hidden-sm')
      this.$printToggleButton.appendTo(li)
      this.$navbarSouth.find('.pull-right .nav').prepend(li)
    }
  }
}
